import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Landmark, ArrowDownToLine, ArrowUpFromLine, ShieldCheck } from "lucide-react";

export const money = (c, n) => (c || "USD") === "USD" ? `$${Number(n || 0).toLocaleString()}` : `${c} ${Number(n || 0).toLocaleString()}`;

function Stat({ label, value, icon: Icon, tone, testid, sub }) {
  return (
    <Card className="gold-ring glass p-5" data-testid={testid}>
      <div className="flex items-center justify-between"><p className="label-eyebrow">{label}</p><Icon className={`h-4 w-4 ${tone}`} /></div>
      <p className={`mt-2 font-mono-data text-2xl font-700 ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-[0.65rem] text-muted-foreground" data-testid={`${testid}-sub`}>{sub}</p>}
    </Card>
  );
}

export default function TreasureSummary({ data }) {
  const entries = Object.entries(data.totals || {});
  const [cur, t] = entries[0] || ["USD", { opening: 0, inflow: 0, outflow: 0, balance: 0 }];
  return (
    <div className="space-y-4" data-testid="treasure-summary">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={`${data.scope} balance`} value={money(cur, t.balance)} icon={Landmark} tone="text-[hsl(var(--gold))]" testid="treasure-balance" sub={t.opening ? `incl. ${money(cur, t.opening)} brought forward` : null} />
        <Stat label="Inflow (verified)" value={money(cur, t.inflow)} icon={ArrowDownToLine} tone="text-emerald-600" testid="treasure-inflow" />
        <Stat label="Outflow (expenses)" value={money(cur, t.outflow)} icon={ArrowUpFromLine} tone="text-rose-600" testid="treasure-outflow" />
        <Stat label="Proofs to verify" value={String(data.pending_proofs || 0)} icon={ShieldCheck} tone="text-amber-600" testid="treasure-pending" />
      </div>
      {entries.length > 1 && <p className="text-xs text-muted-foreground" data-testid="treasure-other-currencies">Also held: {entries.slice(1).map(([c, v]) => money(c, v.balance)).join(" · ")}</p>}
      {data.breakdown?.length > 0 && (
        <Card className="gold-ring glass p-4" data-testid="treasure-breakdown">
          <p className="label-eyebrow mb-3">Galleon & frigates</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {data.breakdown.map((b) => { const [c, v] = Object.entries(b.totals)[0] || ["USD", { opening: 0, inflow: 0, outflow: 0, balance: 0 }]; return (
              <div key={b.scope} className="rounded-lg bg-secondary/60 p-3" data-testid={`breakdown-${b.scope.toLowerCase()}`}>
                <div className="flex items-center justify-between"><p className="font-display font-700">{b.scope}</p>{b.pending > 0 && <Badge className="bg-amber-500 text-[0.6rem] text-white">{b.pending} to verify</Badge>}</div>
                <p className="mt-1 font-mono-data text-lg font-700 text-[hsl(var(--gold))]">{money(c, v.balance)}</p>
                <p className="text-[0.65rem] text-muted-foreground">{v.opening ? `b/f ${money(c, v.opening)} · ` : ""}in {money(c, v.inflow)} · out {money(c, v.outflow)}</p>
              </div>); })}
          </div>
        </Card>
      )}
    </div>
  );
}
